import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AccountGroupApiService,
  GetMultiRequest,
  GetMultiResponseAccountGroupContainer,
  ProjectionFilter,
  GetMultiResponse,
} from '@vendasta/account-group';
import { ObservableWorkState } from '@vendasta/rx-utils/work-state';
import { BehaviorSubject, Observable, ReplaySubject, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CRMCompanyApiService, CrmObject, GetMultiCrmObjectRequest, GetMultiCrmObjectResponse } from '@vendasta/crm';
import { PARTNER_ID_TOKEN } from '../tokens';
import { CrmFieldService, PlatformExtensionFieldIds, StandardIds } from '@galaxy/crm/static';
import { DETAILS_CONFIG_TOKEN } from '../pipeline-card/tokens';

class PipelineBusinessIdentifier {
  loading$$ = new BehaviorSubject(true);
  accountGroupName$$ = new ReplaySubject<string>(1);
  companyId$$ = new ReplaySubject<string>(1);
  companyName$$ = new ReplaySubject<string>(1);
}

@Injectable()
export class PipelineBusinessNameService {
  readonly businessIdentifiers = new Map<string, PipelineBusinessIdentifier>();
  readonly companyLoadingWorkState = new ObservableWorkState<GetMultiCrmObjectResponse>();
  readonly accountLoadingWorkState = new ObservableWorkState<GetMultiResponse>();

  private readonly latestIds$$ = new BehaviorSubject<string[]>(null);

  private readonly accountGroupSDK = inject(AccountGroupApiService);
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly companyAPIService = inject(CRMCompanyApiService);
  private readonly crmFieldService = inject(CrmFieldService);
  private readonly detailsConfig = inject(DETAILS_CONFIG_TOKEN);

  constructor() {
    this.companyLoadingWorkState.workResults$
      .pipe(
        takeUntilDestroyed(),
        map((results) => results.crmObjects ?? []),
      )
      .subscribe((results) => {
        this.setCompanyNames(results);
      });
    this.companyLoadingWorkState.isLoading$
      .pipe(
        takeUntilDestroyed(),
        filter((isLoading) => isLoading === false),
      )
      .subscribe(() => this.businessIdentifiers.forEach((bn) => bn.loading$$.next(false)));

    this.accountLoadingWorkState.workResults$
      .pipe(
        takeUntilDestroyed(),
        map((results) => results.accountGroups ?? []),
      )
      .subscribe((results) => {
        this.addInfoFromAGGetMulti(results);
      });
    this.accountLoadingWorkState.isLoading$
      .pipe(
        takeUntilDestroyed(),
        filter((isLoading) => isLoading === false),
      )
      .subscribe(() => this.businessIdentifiers.forEach((bn) => bn.loading$$.next(false)));
  }

  public enqueueAccountLoad(agIds: string[]): void {
    const agIdsToGet = [];
    agIds.forEach((agid) => {
      if (!this.businessIdentifiers.has(agid)) {
        agIdsToGet.push(agid);
      }
    });

    if (agIdsToGet.length <= 0) {
      return;
    }

    this.latestIds$$.next(agIdsToGet);

    this.createBusinessObjectsForNewIds(agIdsToGet);

    if (this.detailsConfig.showCRMDetails) {
      this.loadCompanies(agIdsToGet);
    } else {
      this.loadAccounts(agIdsToGet);
    }
  }

  private loadCompanies(agIds: string[]): void {
    const projectionFilter = new ProjectionFilter({ napData: true, accountGroupExternalIdentifiers: true });

    const req = <GetMultiRequest>{
      accountGroupIds: agIds,
      projectionFilter,
    };

    this.companyLoadingWorkState.startWork(
      this.accountGroupSDK.getMulti(req).pipe(
        catchError((err) => {
          if (err.status === 403) {
            const failedAccountGroupIds = err.error.details[0].failures[0].identifiers.account_group_id.values;
            this.setEmptyBusinessObjectsForIds(failedAccountGroupIds);
            return this.retryGetMulti(failedAccountGroupIds, agIds, projectionFilter);
          }
          return throwError(() => err);
        }),
        tap((res) => {
          this.addInfoFromAGGetMulti(res.accountGroups);
        }),
        withLatestFrom(this.partnerId$),
        switchMap(([res, partnerId]) => {
          const companyIds = res.accountGroups
            ?.filter((ag) => !!ag.accountGroup?.accountGroupExternalIdentifiers?.platformCrmCompanyId)
            .map((ag) => ag.accountGroup?.accountGroupExternalIdentifiers?.platformCrmCompanyId);
          if (!companyIds.length) {
            return of({} as GetMultiCrmObjectResponse);
          }
          const req = <GetMultiCrmObjectRequest>{
            namespace: partnerId,
            crmObjectIds: companyIds,
          };
          return this.companyAPIService.getMultiCompany(req);
        }),
        catchError(() => {
          return of({} as GetMultiCrmObjectResponse);
        }),
      ),
    );
  }

  private loadAccounts(agIds: string[]): void {
    const projectionFilter = new ProjectionFilter({ napData: true });

    const req = <GetMultiRequest>{
      accountGroupIds: agIds,
      projectionFilter,
    };

    this.accountLoadingWorkState.startWork(
      this.accountGroupSDK.getMulti(req).pipe(
        catchError((err) => {
          if (err.status === 403) {
            const failedAccountGroupIds = err.error.details[0].failures[0].identifiers.account_group_id.values;
            this.setEmptyBusinessObjectsForIds(failedAccountGroupIds);
            return this.retryGetMulti(failedAccountGroupIds, agIds, projectionFilter);
          }
          return throwError(() => err);
        }),
      ),
    );
  }

  createBusinessObjectsForNewIds(ids: string[]): void {
    ids.forEach((id) =>
      this.businessIdentifiers.set(id, <PipelineBusinessIdentifier>{
        loading$$: new BehaviorSubject(true),
        accountGroupName$$: new ReplaySubject<string>(1),
        companyId$$: new ReplaySubject<string>(1),
        companyName$$: new ReplaySubject<string>(1),
      }),
    );
    this.setEmptyBusinessObjectsForIds(ids);
  }

  private addInfoFromAGGetMulti(newAccountGroups: GetMultiResponseAccountGroupContainer[]): void {
    newAccountGroups.forEach((ag) => {
      if (ag.accountGroup?.accountGroupId) {
        this.getBusinessNameFromMapSafely(ag.accountGroup.accountGroupId).loading$$.next(false);
        this.getBusinessNameFromMapSafely(ag.accountGroup.accountGroupId).accountGroupName$$.next(
          ag.accountGroup.napData.companyName ?? '',
        );
        this.getBusinessNameFromMapSafely(ag.accountGroup.accountGroupId).companyId$$.next(
          ag.accountGroup.accountGroupExternalIdentifiers?.platformCrmCompanyId ?? '',
        );
      }
    });
  }

  private setEmptyBusinessObjectsForIds(ids: string[]): void {
    ids.forEach((id) => {
      const business = this.businessIdentifiers.get(id);
      business.accountGroupName$$.next('');
      business.companyId$$.next('');
      business.companyName$$.next('');
    });
  }

  private getBusinessNameFromMapSafely(ag: string): PipelineBusinessIdentifier {
    let business = this.businessIdentifiers.get(ag);
    if (!business) {
      business = new PipelineBusinessIdentifier();
      this.businessIdentifiers.set(ag, business);
    }
    return business;
  }

  public getCompanyName$(accountGroupId: string): Observable<string> {
    const businessIdentifier = this.businessIdentifiers.get(accountGroupId);
    return businessIdentifier.companyName$$;
  }

  public getCompanyId$(accountGroupId: string): Observable<string> {
    const businessIdentifier = this.businessIdentifiers.get(accountGroupId);
    return businessIdentifier.companyId$$;
  }

  private setCompanyNames(companies: CrmObject[]): void {
    companies.forEach((company) => {
      const accountGroupId =
        this.crmFieldService.getFieldValueFromCrmObject(company, PlatformExtensionFieldIds.AccountGroupID)
          ?.stringValue ?? '';
      this.getBusinessNameFromMapSafely(accountGroupId).companyName$$.next(
        this.crmFieldService.getFieldValueFromCrmObject(company, StandardIds.CompanyName)?.stringValue ?? '',
      );
    });
  }

  private getValidAccountGroupIds(allAccountGroupIds: string[], failedAccountGroupIds: string[]): string[] {
    return allAccountGroupIds.filter((id) => !failedAccountGroupIds.includes(id));
  }

  private retryGetMulti(
    failedAccountGroupIds: string[],
    allAccountGroupIds: string[],
    projectionFilter: ProjectionFilter,
  ): Observable<GetMultiResponse> {
    const validAccountGroupIds = this.getValidAccountGroupIds(allAccountGroupIds, failedAccountGroupIds);
    if (!validAccountGroupIds.length) {
      return of({} as GetMultiResponse);
    }
    const req = <GetMultiRequest>{
      accountGroupIds: validAccountGroupIds,
      projectionFilter,
    };
    return this.accountGroupSDK.getMulti(req);
  }
}
